import { type AppSettings, type SettingsKey, type UpdateSettingResponse, settingsIssues } from '@hearth/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { useState } from 'react';
import { Button, Card, CardBody, CardHeader, Field, Input, MultiSelect, Select, Switch, Textarea, useToast } from '../../components/ui';
import { api, errorMessage, fieldErrors } from '../../lib/api';

export type FieldSpec = {
  /** Dot path inside the settings group, e.g. "sendWindow.startHour". */
  path: string;
  label: string;
  hint?: string;
  kind: 'text' | 'textarea' | 'number' | 'switch' | 'select' | 'multiselect' | 'weekdays' | 'lines';
  options?: readonly { value: string; label: string }[];
  min?: number;
  max?: number;
};

export type FormSpec<K extends SettingsKey> = { key: K; title: string; description?: string; fields: readonly FieldSpec[] };

type Json = string | number | boolean | null | Json[] | { [key: string]: Json };

function getPath(value: Json, path: string): Json {
  return path.split('.').reduce<Json>((node, part) => (node !== null && typeof node === 'object' && !Array.isArray(node) ? (node[part] ?? null) : null), value);
}

function setPath(value: Json, path: string, next: Json): Json {
  const [head, ...rest] = path.split('.');
  const node = value !== null && typeof value === 'object' && !Array.isArray(value) ? value : {};
  return { ...node, [head!]: rest.length === 0 ? next : setPath(node[head!] ?? {}, rest.join('.'), next) };
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * One typed settings group as a form. Client-side cross-field rules run before saving (same pure function as
 * the API), server issues come back per field.
 */
export function SettingsForm<K extends SettingsKey>({ spec, value, canEdit }: { spec: FormSpec<K>; value: AppSettings[K]; canEdit: boolean }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Json>(value as Json);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const dirty = JSON.stringify(draft) !== JSON.stringify(value);

  const save = useMutation({
    mutationFn: () => api.put<UpdateSettingResponse<K>>(`/api/settings/${spec.key}`, draft),
    onSuccess: async (result) => {
      setErrors({});
      toast.success(result.recomputeQueued ? `${spec.title} saved — leads are being recalculated` : `${spec.title} saved`);
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
      await queryClient.invalidateQueries({ queryKey: ['meta'] });
    },
    onError: (error) => {
      setErrors(fieldErrors(error));
      toast.error(errorMessage(error));
    },
  });

  const submit = () => {
    const issues = settingsIssues(spec.key, draft as AppSettings[K]);
    if (issues.length > 0) {
      setErrors(Object.fromEntries(issues.map((issue) => [issue.path, issue.message])));
      return;
    }
    save.mutate();
  };

  const update = (path: string, next: Json) => setDraft(setPath(draft, path, next));

  return (
    <Card>
      <CardHeader title={spec.title} description={spec.description} />
      <CardBody>
        <form
          className="grid gap-3 sm:grid-cols-2"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <fieldset disabled={!canEdit} className="contents">
            {spec.fields.map((field) => {
              const current = getPath(draft, field.path);
              const error = errors[field.path] ?? null;
              const wide = field.kind === 'textarea' || field.kind === 'lines' || field.kind === 'weekdays';
              if (field.kind === 'switch') {
                return (
                  <div key={field.path} className="sm:col-span-2">
                    <Switch checked={current === true} onChange={(next) => update(field.path, next)} label={field.label} description={field.hint} disabled={!canEdit} />
                  </div>
                );
              }
              return (
                <Field key={field.path} label={field.label} hint={field.hint} error={error} className={wide ? 'sm:col-span-2' : undefined}>
                  {field.kind === 'text' ? (
                    <Input value={typeof current === 'string' ? current : ''} onChange={(event) => update(field.path, event.target.value)} />
                  ) : field.kind === 'textarea' ? (
                    <Textarea rows={3} value={typeof current === 'string' ? current : ''} onChange={(event) => update(field.path, event.target.value)} />
                  ) : field.kind === 'number' ? (
                    <Input
                      type="number"
                      min={field.min}
                      max={field.max}
                      value={typeof current === 'number' ? current : ''}
                      onChange={(event) => update(field.path, event.target.value === '' ? null : Number(event.target.value))}
                    />
                  ) : field.kind === 'select' ? (
                    <Select value={typeof current === 'string' ? current : ''} onChange={(event) => update(field.path, event.target.value)}>
                      {(field.options ?? []).map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  ) : field.kind === 'multiselect' ? (
                    <MultiSelect
                      label={field.label}
                      options={field.options ?? []}
                      value={Array.isArray(current) ? current.filter((v): v is string => typeof v === 'string') : []}
                      onChange={(next) => update(field.path, next)}
                    />
                  ) : field.kind === 'weekdays' ? (
                    <div className="flex flex-wrap gap-1">
                      {WEEKDAYS.map((day, index) => {
                        const days = Array.isArray(current) ? current.filter((v): v is number => typeof v === 'number') : [];
                        const on = days.includes(index + 1);
                        return (
                          <Button
                            key={day}
                            size="sm"
                            variant={on ? 'primary' : 'secondary'}
                            onClick={() => update(field.path, on ? days.filter((d) => d !== index + 1) : [...days, index + 1].sort())}
                          >
                            {day}
                          </Button>
                        );
                      })}
                    </div>
                  ) : (
                    <Textarea
                      rows={5}
                      value={Array.isArray(current) ? current.filter((v): v is string => typeof v === 'string').join('\n') : ''}
                      onChange={(event) => update(field.path, event.target.value.split('\n'))}
                    />
                  )}
                </Field>
              );
            })}
          </fieldset>
          {canEdit && (
            <div className="flex items-center gap-2 sm:col-span-2">
              <Button type="submit" variant="primary" icon={Save} disabled={!dirty} loading={save.isPending}>
                Save
              </Button>
              {dirty && (
                <Button variant="ghost" onClick={() => setDraft(value as Json)}>
                  Discard changes
                </Button>
              )}
            </div>
          )}
        </form>
      </CardBody>
    </Card>
  );
}
